/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSelector } from '@reduxjs/toolkit';
import { SLICE } from 'modules/dashboard/constants';
import { initialState } from './slice';
//
const selectDomain = (state) => state[SLICE.WORKBOOK_REPORT] || initialState;

/**
 * Getting summary widget booking date state to summary widget component
 */
export const selectWorkbookBookingDate = createSelector(
  [selectDomain],
  (state) => state.workbookBookingDate
);

/**
 * Getting date comparison state to summary widget component
 */
export const selectDateComparison = createSelector([selectDomain], (state) => state.dateComparison);

/**
 * Getting summary report filter list state to summary widget component
 */
export const selectWorkbookFilterList = createSelector(
  [selectDomain],
  (state) => state.workbookFilterList
);

/**
 * Getting summary report filter list state to summary widget component
 */
export const selectWorkbookHotelFilterList = createSelector(
  [selectDomain],
  (state) => state.workbookHotelFilterList
);
/**
 * Getting alignment state to summary widget component
 */
export const selectAlignment = createSelector([selectDomain], (state) => state.alignment);
/**
 *  Getting summary widget alignment toggle state to summary widget component
 */
export const selectWorkbookAlignmentToggle = createSelector(
  [selectDomain],
  (state) => state.workbookAlignmentToggle
);
/**
 * Getting summary otb state for summary widget component
 */
export const selectWorkbookOtb = createSelector([selectDomain], (state) => state?.workbookOtb);

/**
 *Getting is summary custom date state for summary widget component
 */
export const selectWorkbookCustomDate = createSelector(
  [selectDomain],
  (state) => state?.workbookCustomDate
);

/**
 *Getting is summary start date state for summary widget component for custom date comparison
 */
export const selectWorkbookStartDate = createSelector(
  [selectDomain],
  (state) => state?.workbookStartDate
);

/**
 *Getting is summary end date state for summary widget component for custom date comparison
 */
export const selectWorkbookEndDate = createSelector(
  [selectDomain],
  (state) => state?.workbookEndDate
);
/**
 * Getting symmetric comparison toggle state for summary widget component
 */
export const selectSymmetricComparisonToggle = createSelector(
  [selectDomain],
  (state) => state?.symmetricComparisonToggle
);
/**
 * Getting workbook selected date for workbook widget component
 */
export const selectWorkbookDateSelected = createSelector(
  [selectDomain],
  (state) => state?.workbookDateSelected
);
/**
 * Getting pdf exported object into the component
 */
export const selectPdfExportedObject = createSelector([selectDomain], (state) => state?.exportData);
/**
 * Getting loading state into the component
 */
export const selectLoader = createSelector([selectDomain], (state) => state?.loading);
/**
 *  Getting generated workbook tab url
 */
export const selectWorkbookUrl = createSelector([selectDomain], (state) => state.workbookUrl);
/**
 *  Getting symmetric comparison visual display state to the component
 */
export const selectSymmetricComparisonIsDisplay = createSelector(
  [selectDomain],
  (state) => state.symmetricComparisonIsDisplay
);
/**
 *  Getting D1 display state to the component
 */
export const selectD1SetIsDisplay = createSelector([selectDomain], (state) => state.d1SetIsDisplay);
/**
 *  Getting D2 display state to the component
 */
export const selectD2SetIsDisplay = createSelector([selectDomain], (state) => state.d2SetIsDisplay);
/**
 *  Getting D3 display state to the component
 */
export const selectD3SetIsDisplay = createSelector([selectDomain], (state) => state.d3SetIsDisplay);
/**
 *  Getting selected enable secondary hotel state to the component
 */
export const selectEnableSecondaryHotelGroups = createSelector(
  [selectDomain],
  (state) => state.enableSecondaryHotelGroups
);
/**
 *  Getting hotel view focus on state to dashboard component
 */
export const selectHotelFocusOn = createSelector([selectDomain], (state) => state.hotelFocusOn);
/**
 *Getting normalized view state for the component
 */
export const selectNormalizedView = createSelector(
  [selectDomain],
  (state) => state?.normalizedView
);
/**
 *Getting workbook hotel comparison visual state for the component
 */
export const selectWorkbookHotelVisual = createSelector(
  [selectDomain],
  (state) => state?.workbookHotelVisuals
);
/**
 *Getting is workbook hotel comparison state for the component
 */
export const selectWorkbookHotelComparison = createSelector(
  [selectDomain],
  (state) => state?.isWorkbookHotelComparison
);
/**
 *Getting is rate plan state for the component
 */
export const selectIsRatePlan = createSelector([selectDomain], (state) => state?.isRatePlan);
/**
 *Getting is rate plan visuals state for the component
 */
export const selectRatePlanVisuals = createSelector(
  [selectDomain],
  (state) => state?.ratePlanVisuals
);

/**
 * Getting rate plan filter list state to dashboard component
 */
export const selectRatePlanFilterList = createSelector(
  [selectDomain],
  (state) => state.ratePlanFilterList
);
/**
 *  Getting is original focus on state to the component
 */
export const selectOriginalHotelFocusOn = createSelector(
  [selectDomain],
  (state) => state.originalFocusOn
);
/**
 *Getting is is travel agent state for the component
 */
export const selectIsTravelAgent = createSelector([selectDomain], (state) => state?.isTravelAgent);
/**
 *Getting is detailed  state for the component
 */
export const selectIsDetailed = createSelector([selectDomain], (state) => state?.isDetailed);
/**
 *Getting  travel Agent Details On Visuals state for the component
 */
export const selectTravelAgentDetailsOnVisuals = createSelector(
  [selectDomain],
  (state) => state?.travelAgentDetailsOnVisuals
);
/**
 *Getting travel Agent Details On Visuals state for the component
 */
export const selectTravelAgentDetailsOffVisuals = createSelector(
  [selectDomain],
  (state) => state?.travelAgentDetailsOffVisuals
);

/**
 * Getting Is Secondary Hotel Set Over Time state into dashboard component
 */
export const selectIsSecondaryHotelSetOverTime = createSelector(
  [selectDomain],
  (systemState) => systemState.isSecondaryHotelSetOverTime
);

/**
 * Getting Is Secondary Hotel Set Over Time Disabled state into dashboard component
 */
export const selectIsSecondaryHotelSetOverTimeDisabled = createSelector(
  [selectDomain],
  (systemState) => systemState.isSecondaryHotelSetOverTimeDisabled
);

/**
 * Getting Invalid Secondary Hotel List state into dashboard component
 */
export const selectInvalidSecondaryHotelList = createSelector(
  [selectDomain],
  (systemState) => systemState.invalidSecondaryHotelList
);
/**
 *Getting is room nights pace state for the component
 */
export const selectIsRoomNightsPace = createSelector(
  [selectDomain],
  (state) => state?.isRoomNightsPace
);

/**
 * Getting rate plan data state for the component
 */
export const selectRatePlanData = createSelector([selectDomain], (state) => state.ratePlanData);

/**
 * Getting consortia data state for the component
 */
export const selectConsortiaData = createSelector([selectDomain], (state) => state.consortiaData);

/**
 * Getting pace data state for the component
 */
export const selectPaceData = createSelector([selectDomain], (state) => state.paceData);

/**
 *Getting iata export data state for the component
 */
export const selectIataData = createSelector([selectDomain], (state) => state?.iataData);
/**
 *Getting room type data state for the component
 */
 export const selectRoomTypeData = createSelector([selectDomain], (state) => state?.roomTypeData);
/**
 *  Getting generated export tab url
 */
export const selectExportUrl = createSelector([selectDomain], (state) => state.exportUrl);
/**
 * Get is leap selection state
 */
export const selectIsDashboardLeapComparison = createSelector(
  [selectDomain],
  (systemState) => systemState.isLeapSelection
);
/**
 * Get is room type state
 */
export const selectIsRoomType = createSelector(
  [selectDomain],
  (state) => state.isRoomType
);
